import { db } from './db.js'
import { createProduto, updateProduto, deleteProduto, fetchProdutos } from './syncManager.js'
import { createNotaFiscal, listNotasFiscais } from './notasFiscais.js'

export const PRODUCT_TYPES = {
  ferramenta: 'ferramenta',
  material: 'material',
  epi: 'epi'
}

export function getProductTypeLabel(tipo) {
  switch (tipo) {
    case PRODUCT_TYPES.ferramenta:
      return 'Ferramenta'
    case PRODUCT_TYPES.material:
      return 'Material'
    case PRODUCT_TYPES.epi:
      return 'EPI'
    default:
      return '—'
  }
}

export function getProductSubLabel(p) {
  if (p?.tipo !== PRODUCT_TYPES.epi) return null
  return p.reutilizavel ? 'Reutilizável' : 'Descartável'
}

export async function listProducts({ q = '', tipo = 'todos', status = 'todos', localizacao = '', nfNumero = '' } = {}) {
  const query = q.trim().toLowerCase()
  const locQuery = localizacao.trim().toLowerCase()
  const nfQuery = nfNumero.trim().toLowerCase()
  const all = await fetchProdutos()

  // Se busca por NF, carregar NFs para fazer o match
  let nfMap = {}
  if (nfQuery) {
    const allNfs = await listNotasFiscais({ status: 'ativa' })
    nfMap = Object.fromEntries(
      allNfs
        .filter(nf => (nf.numero ?? '').toLowerCase().includes(nfQuery))
        .map(nf => [nf.id, nf])
    )
  }

  return all
    .filter((p) => {
      const pTipo = p.tipo || p.categoria
      const pStatus = p.status || 'ativo'
      if (tipo !== 'todos' && pTipo !== tipo) return false
      if (status !== 'todos' && pStatus !== status) return false
      if (locQuery) {
        const locMatch = (
          (p.localizacao ?? '').toLowerCase().includes(locQuery) ||
          (p.locCorredor ?? '').toLowerCase().includes(locQuery) ||
          (p.locPrateleira ?? '').toLowerCase().includes(locQuery) ||
          (p.locGaveta ?? '').toLowerCase().includes(locQuery)
        )
        if (!locMatch) return false
      }
      if (nfQuery) {
        // Só inclui se tem NF e o número da NF corresponde à busca
        if (!p.notaFiscalId || !nfMap[p.notaFiscalId]) return false
      }
      if (!query) return true
      return (
        (p.nome ?? '').toLowerCase().includes(query) ||
        (p.codigo ?? '').toLowerCase().includes(query)
      )
    })
    .sort((a, b) => (a.nome ?? '').toLowerCase().localeCompare((b.nome ?? '').toLowerCase()))
}

export async function createProduct(payload) {
  const product = {
    id: crypto.randomUUID(),
    tipo: payload.tipo,
    reutilizavel: payload.tipo === PRODUCT_TYPES.epi ? Boolean(payload.reutilizavel) : null,
    nome: (payload.nome ?? '').trim(),
    codigo: (payload.codigo ?? '').trim() || null,
    descricao: (payload.descricao ?? '').trim() || null,
    unidade: payload.unidade ?? 'unidade',
    quantidade: Number(payload.quantidade ?? 0),
    estoqueMinimo: Number(payload.estoqueMinimo ?? 0),
    quantidadeMin: Number(payload.estoqueMinimo ?? 0),
    ca: payload.ca ? String(payload.ca).trim() : null,
    foto: payload.foto || null,
    status: 'ativo',
    // extras JSONB
    custoUnitario: payload.custoUnitario !== undefined && payload.custoUnitario !== ''
      ? Number(payload.custoUnitario)
      : null,
    // Localização no almoxarifado (v4.0.0)
    localizacao: (payload.localizacao ?? '').trim() || null,
    locCorredor: (payload.locCorredor ?? '').trim() || null,
    locPrateleira: (payload.locPrateleira ?? '').trim() || null,
    locGaveta: (payload.locGaveta ?? '').trim() || null,
    locObservacao: (payload.locObservacao ?? '').trim() || null
  }

  // Se houver dados de Nota Fiscal, cria a NF primeiro
  if (payload.nfNumero?.trim()) {
    const nfId = await createNotaFiscal({
      numero: payload.nfNumero,
      serie: payload.nfSerie,
      dataEmissao: payload.nfDataEmissao,
      cnpjFornecedor: payload.nfCnpjFornecedor,
      fornecedorNome: payload.nfFornecedorNome,
      valorTotal: payload.nfValorTotal,
      arquivoBase64: payload.nfArquivoBase64,
      observacao: payload.nfObservacao
    })
    product.notaFiscalId = nfId
  }

  return createProduto(product)
}

export async function updateProduct(id, patch) {
  const updates = {}
  if (patch.tipo !== undefined) updates.tipo = patch.tipo
  if (patch.reutilizavel !== undefined) updates.reutilizavel = Boolean(patch.reutilizavel)
  if (patch.nome !== undefined) updates.nome = (patch.nome ?? '').trim()
  if (patch.codigo !== undefined) updates.codigo = (patch.codigo ?? '').trim() || null
  if (patch.descricao !== undefined) updates.descricao = (patch.descricao ?? '').trim() || null
  if (patch.unidade !== undefined) updates.unidade = patch.unidade
  if (patch.quantidade !== undefined) updates.quantidade = Number(patch.quantidade ?? 0)
  if (patch.estoqueMinimo !== undefined) { updates.estoqueMinimo = Number(patch.estoqueMinimo ?? 0); updates.quantidadeMin = Number(patch.estoqueMinimo ?? 0) }
  if (patch.ca !== undefined) updates.ca = patch.ca ? String(patch.ca).trim() : null
  if (patch.dataValidade !== undefined) updates.dataValidade = patch.dataValidade ? String(patch.dataValidade).trim() : null
  if (patch.foto !== undefined) updates.foto = patch.foto || null
  if (patch.custoUnitario !== undefined) updates.custoUnitario = (patch.custoUnitario === '' || patch.custoUnitario === null)
    ? null
    : Number(patch.custoUnitario)
  // Localização no almoxarifado (v4.0.0)
  if (patch.localizacao !== undefined) updates.localizacao = (patch.localizacao ?? '').trim() || null
  if (patch.locCorredor !== undefined) updates.locCorredor = (patch.locCorredor ?? '').trim() || null
  if (patch.locPrateleira !== undefined) updates.locPrateleira = (patch.locPrateleira ?? '').trim() || null
  if (patch.locGaveta !== undefined) updates.locGaveta = (patch.locGaveta ?? '').trim() || null
  if (patch.locObservacao !== undefined) updates.locObservacao = (patch.locObservacao ?? '').trim() || null
  if (patch.notaFiscalId !== undefined) updates.notaFiscalId = patch.notaFiscalId || null

  return updateProduto(id, updates)
}

export async function getProduct(id) {
  const all = await fetchProdutos()
  return all.find(p => p.id === id) || null
}

export async function deleteProduct(id) {
  return deleteProduto(id)
}
